import React, { useState } from "react";
import IAuthentication from "../../core/contracts/IAuthentication";
import LoginPage from "../../screens/LoginPage";
import { useHubx } from "../../HubX";
import IAuthenticationResponse from "../../core/contracts/IAuthenticationResponse";
import { useHistory } from "react-router-dom";
import useAuth from "../../core/hooks/useAuth";
import {
  TEST_BEARER_TOKEN,
  DO_LOGIN,
  FETCH_USER_INFO,
  DO_UPDATE_PUSH_USER_TOKEN,
} from "../../Fedx";
import { LocalCache, SessionCache } from "../../helpers/cache";
import IUserResponse from "../../core/contracts/IUserResponse";
import { useSnackbar } from "notistack";
import { encodeStringToBase64 } from "../../helpers/utility";
import { MAX_AUTO_LOGINS_ATTEMPTS } from "../../core/constants/appGlobals";
import IPushUserToken from "../../core/contracts/IPushUserToken";

type LoginProps = {
  expiredToken?: boolean;
};

function Login({ expiredToken }: LoginProps) {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const hub = useHubx();
  const authProvider = useAuth();
  const toastr = useSnackbar();

  React.useEffect(() => {
    history.replace("/");
    if (expiredToken) {
      toastr.enqueueSnackbar(
        "Ops! O Token de segurança expirou! Por favor, faça login novamente!",
        { variant: "info" }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (loginData: IAuthentication) => {
    setLoading(true);
    tryLogin(loginData);
  };

  let erroStatus = "";
  const tryLogin = async (loginData: IAuthentication, attempt = 1): Promise<unknown> => {
    // eslint-disable-next-line no-console
    try {
      const results = await hub.request(DO_LOGIN, loginData);
      return handleLoginResponse(results, loginData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("doLogin -> error", error);
      if (error && error.data) {
        if (error.status === 417) {
          toastr.enqueueSnackbar(error.data, {
            variant: "error",
          });
          redirectExpiredPass(loginData.username);
          return;
        } else {
          erroStatus = error.data;
          failedLogin(erroStatus);
          return;
        }
      }
      if (attempt < MAX_AUTO_LOGINS_ATTEMPTS) {
        return tryLogin(loginData, attempt + 1);
      } else {
        failedLogin(erroStatus);
      }
    }
  };

  const handleLoginResponse = (
    APIResponse: IAuthenticationResponse,
    previousLogin: IAuthentication
  ) => {
    const { token } = APIResponse;

    if (token) {
      authProvider.set(token);
    }

    testBearer(previousLogin);
  };

  const testBearer = async (previousLogin: IAuthentication) => {
    try {
      await hub.request(TEST_BEARER_TOKEN);
      getUserInfo(previousLogin);
    } catch (error) {
      // eslint-disable-next-line no-console
      authProvider.set(null);
      tryLogin(previousLogin);
    }
  };

  const failedLogin = (erro?: string) => {
    toastr.enqueueSnackbar(
      !erro ? "Não foi possível completar o Login! Tente novamente mais tarde!" : erro,
      {
        variant: "error",
      }
    );
    setLoading(false);
  };

  const getUserInfo = async (previousLogin: IAuthentication) => {
    try {
      const sessionCache = new LocalCache();
      const result: IUserResponse = await hub.request(FETCH_USER_INFO);
      sessionCache.set("user", result);
      updatePushUserToken(result.id);
      redirect();
    } catch (error) {
      failedLogin();
    }
  };

  const updatePushUserToken = async (idUser: number) => {
    try {
      const StorageCache = new LocalCache();
      const tokenPush: string | null = StorageCache.get("push_token");

      const webPushUserToken: IPushUserToken = {
        userId: idUser,
        userPushId: tokenPush ? tokenPush : "",
      };

      await hub.request(DO_UPDATE_PUSH_USER_TOKEN, webPushUserToken);
      StorageCache.delete("push_token");
      // eslint-disable-next-line no-empty
    } catch (error) { }
  };

  const redirect = () => {
    const cache = new SessionCache();
    let prevLocale: any = cache.get<string>("previousLocale");
    if (prevLocale) {
      cache.delete("previousLocale");
      history.push(prevLocale);
      return;
    }
    history.push("/home/");
  };

  const redirectExpiredPass = (username: string) => {
    history.push("/reset/" + encodeStringToBase64("username" + username));
  };

  return <LoginPage loading={loading} onSubmit={doLogin} />;
}

export default Login;
